import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { Typography, Paper, Box, Container, Chip, Divider, Alert, MenuItem, TextField } from '@mui/material';
import { Lock, Fingerprint, Security, VerifiedUser } from '@mui/icons-material';
import LoginForm from './LoginForm';
import LoginBankID from './LoginBankID';
import LoginFrejaID from './LoginFrejaID';
import LoginSITHS from './LoginSITHS';
import LoginSAML from './LoginSAML';
import { MessageToastLogin } from '../Generic';
import { replace } from 'connected-react-router';
import { handleSelectClientAction } from './actions/loginActions';
import { getTranslate } from 'react-localize-redux';
import PlaceholderIMG from '../../assets/images/placeholder.svg';
import MadeBy from './MadeBy';
import { setActiveLanguage, getActiveLanguage, getLanguages } from 'react-localize-redux';

const Login = ({ settings, config, translate, ...props }) => {
	const location = useLocation();
	const dispatch = useDispatch();
	const { client } = useParams();

	const [login_error_message, setLoginError] = useState(props.query?.login_error_message || null);

	const supportedLoginMethods =
		settings.site?.supported_login_methods
			?.split(',')
			.map((method) => method.trim())
			.filter((item) => item !== 'bankid_qrcode') || [];

	const getInitialLoginMethod = () => {
		const fromParams = settings?.params?.login_method;
		const fromQuery = props.query?.login_method;
		const fromSettings = settings?.preferred_login_method;

		if (fromParams) return fromParams;
		if (fromQuery) return fromQuery;
		if (typeof fromSettings !== 'undefined') return fromSettings;
		if (supportedLoginMethods.length > 0) return supportedLoginMethods[0];
		return 'password';
	};
	const [selectedMethod, setSelectedMethod] = useState(getInitialLoginMethod);
	const [recoveryState, setRecoveryState] = useState({
		recovery: false,
		recovery_token: null,
		recovery_medium: null,
		recovery_value: null,
	});

	const query = new URLSearchParams(location.search);
	const recoveryToken = query.get('recovery_token');
	const recoveryMedium = query.get('recovery_medium');
	const recoveryValue = query.get('recovery_value');

	useEffect(() => {
		if (location.pathname.includes('password')) {
			const recoveryMediums = settings.auth?.supported_recovery_password_mediums
				? settings.auth.supported_recovery_password_mediums
						.replace(/,\s*$/, '')
						.split(',')
						.map((item) => item.trim())
						.filter((item) => item.length > 0)
				: [];

			if (recoveryToken && recoveryMedium && recoveryValue && recoveryMediums.includes(recoveryMedium)) {
				setRecoveryState({
					recovery: true,
					recovery_token: recoveryToken,
					recovery_medium: recoveryMedium,
					recovery_value: recoveryValue,
				});

				const updatedParams = new URLSearchParams(location.search);
				updatedParams.delete('recovery_token');
				updatedParams.delete('recovery_medium');
				updatedParams.delete('recovery_value');
				dispatch(replace(`${location.pathname}?${updatedParams.toString()}`));
			} else {
				setRecoveryState({
					recovery: true,
					recovery_token: null,
					recovery_medium: null,
					recovery_value: null,
				});
			}
		}
	}, [settings, location.search, recoveryToken, recoveryMedium, recoveryValue]);

	useEffect(() => {
		if (client) {
			const selectedClient = settings.clients.find((item) => item.short_name === client);
			dispatch(handleSelectClientAction(selectedClient, 'frontoffice'));
		} else if (!client && !props.login) {
			dispatch(handleSelectClientAction(null, 'frontoffice'));
		}
	}, [client]);

	return (
		<Container maxWidth={false} disableGutters>
			<div className={'welcome-presentation-content'}>
				<Box
					sx={{
						position: { xs: 'relative', md: 'fixed' },
						top: 30,
						left: { md: 30 },
						zIndex: 1000,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'flex-start',
					}}
				>
					<img
						src={
							settings?.site?.files?.navbar_logo
								? `${config.apihost}/configuration/files/${settings.site.files.navbar_logo.uuid}`
								: PlaceholderIMG
						}
						height="50"
						alt="Alteri's logo"
					/>
					<Typography variant="caption" color="secondary" sx={{ mt: 1 }}>
						{translate('login_logo_subtitle')}
					</Typography>
				</Box>

				<Box
					sx={{
						position: { xs: 'static', md: 'absolute' },
						right: { md: '5%' },
						top: { md: '45%' },
						transform: { md: 'translateY(-50%)' },
						width: { xs: '90%', sm: '70%', md: '25%' },
						mt: { xs: 5 },
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
					}}
				>
					<Paper elevation={3} className="login-content-right-content-tabs">
						<Typography variant="h6" fontWeight="bold">
							{props.client?.name
								? translate('login_with_client_selected', { name: props.client.name })
								: translate('login_no_client_selected')}
						</Typography>
						<Typography variant="body1">{translate('login_subtitle')}</Typography>
						<Divider sx={{ mt: 1 }} />
						<MessageToastLogin />
						{props.message && props.message.length > 0 ? (
							<Box sx={{ mt: 1 }}>
								<Alert severity="warning">{props.message}</Alert>
							</Box>
						) : login_error_message ? (
							<Box sx={{ mt: 1 }}>
								<Alert severity="warning">{translate(login_error_message)}</Alert>
							</Box>
						) : null}
						<Box mt={3}>
							{selectedMethod === 'password' && (
								<LoginForm
									recovery={recoveryState.recovery}
									recovery_token={recoveryState.recovery_token}
									recovery_medium={recoveryState.recovery_medium}
									recovery_value={recoveryState.recovery_value}
									type="login"
								/>
							)}
							{selectedMethod === 'bankid' && <LoginBankID />}
							{selectedMethod === 'frejaid' && <LoginFrejaID />}
							{selectedMethod === 'siths' && <LoginSITHS />}
							{selectedMethod === 'saml' && <LoginSAML />}
						</Box>
						{props.languages.length > 1 && (
							<Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
								<TextField
									select
									label={translate('generic_select')}
									value={props.currentLanguage?.code || ''}
									onChange={(e) => dispatch(setActiveLanguage(e.target.value))}
									helperText={translate('login_select_preferred_language')}
									margin="normal"
									variant="outlined"
									sx={{ width: 250 }}
								>
									{props.languages.map((option) => (
										<MenuItem key={option.code} value={option.code}>
											{translate('language_' + option.label)}
										</MenuItem>
									))}
								</TextField>
							</Box>
						)}
						{supportedLoginMethods.length > 1 && (
							<Box mt={2} display="flex" flexDirection="column" alignItems="center">
								<Typography variant="body1" gutterBottom>
									{translate('login_supported_methods')}
								</Typography>
								<Box display="flex" gap={1} justifyContent="center">
									{supportedLoginMethods.map((method, key) => {
										const icons = {
											password: <Lock />,
											bankid: <Fingerprint />,
											frejaid: <VerifiedUser />,
											siths: <Security />,
										};
										return (
											<Chip
												key={key}
												label={method.charAt(0).toUpperCase() + method.slice(1)}
												icon={icons[method] || <Lock />}
												clickable
												color={selectedMethod === method ? 'primary' : 'default'}
												onClick={() => setSelectedMethod(method)}
											/>
										);
									})}
								</Box>
							</Box>
						)}
						<Divider sx={{ mt: 2 }} />
						<Box mt={1}>
							<MadeBy />
						</Box>
					</Paper>
				</Box>
			</div>
		</Container>
	);
};

const mapStateToProps = (state) => ({
	translate: getTranslate(state.localize),
	settings: state.settings,
	client: state.login.client,
	login: state.login.login,
	query: state.router.location?.query,
	config: state.config,
	message: state.login.message,
	currentLanguage: getActiveLanguage(state.localize),
	languages: getLanguages(state.localize).filter((test) => test.code !== 'raw'),
});

export default connect(mapStateToProps, { handleSelectClientAction })(Login);
